import TYPES from './types';
import store from 'src/store';
import ListsService from 'src/services/lists/ListsService';
import ProductService from 'src/services/products/ProductService';
import TopProductsService from 'src/services/products/TopProductsService';
import FrequentlyPurchasedService from 'src/services/products/FrequentlyPurchasedService';
import ListModel from 'src/model/list.model';
import ListItemModel from 'src/model/listItem.model';
import { IListsError, IListsSuccess } from './reducers';
import { listsIsPendingListsSel } from './selectors';
import { listsActions } from 'src/store/modules/lists';
import i18n from 'src/translation/i18n';

const INITIAL_ITEM: ListItemModel = {
  listItemNumber: 0,
  manufacturerCode: '',
  name: '',
  quantity: 1,
  shipmentMarking: '',
  warrantyProvider: '',
  dispatch: '',
  inStock: false,
  total: null,
  price: null,
  retailPrice: null,
  image: '',
  description: '',
  itemDetailsState: 'pending',
  replacementMfc: '',
  replacementPart: '',
  alternateProductManufacturer: '',
  alternateProductNumber: '',
  oemProductManufacturer: '',
  oemProductNumber: '',
  warehouseDefault:null
};

const setIsPending = (isPending: boolean) => {
  return {
    type: TYPES.SET_IS_PENDING,
    payload: isPending ? isPending : false
  };
};

const setIsPendingLists = (isPending: boolean) => {
  return {
    type: TYPES.SET_IS_PENDING_LISTS,
    payload: isPending ? isPending : false
  };
};

const setIsUpdating = (isUpdating: boolean) => {
  return {
    type: TYPES.SET_IS_UPDATING,
    payload: isUpdating ? isUpdating : false
  };
};

const setError = (error?: IListsError) => {
  return {
    type: TYPES.SET_ERROR,
    payload: error
      ? error
      : {
          hasFailed: false,
          message: ''
        }
  };
};

const setSuccess = (succed?: IListsSuccess) => {
  return {
    type: TYPES.SET_SUCCESS,
    payload: succed
      ? succed
      : {
          hasSucced: false,
          message: ''
        }
  };
};

const setLists = (lists: ListModel[]) => {
  return {
    type: TYPES.SET_LISTS,
    payload: lists ? lists : false
  };
};

const clearCurrentList = () => {
  return {
    type: TYPES.CLEAR_CURRENT_LIST
  };
};

const setCurrentListNumber = (id: number) => {
  return {
    type: TYPES.SET_CURRENT_LIST_NUMBER,
    payload: id ? id : false
  };
};

const setCurrentListFavorite = (isFavorite: boolean) => {
  return {
    type: TYPES.SET_CURRENT_LIST_FAVORITE,
    payload: isFavorite ? isFavorite : false
  };
};

const setCurrentListName = (name: string) => {
  return {
    type: TYPES.SET_CURRENT_LIST_NAME,
    payload: name ? name : false
  };
};

const setItems = (items: { [key: number]: ListItemModel }) => {
  return {
    type: TYPES.SET_ITEMS,
    payload: items
  };
};

const changeItem = (item: any) => {
  return {
    type: TYPES.UPDATE_ITEM,
    payload: item
  };
};

const removeItem = (itemId: number) => {
  return {
    type: TYPES.REMOVE_ITEM,
    payload: itemId
  };
};

const getList = (id: number) => {
  let items: { [key: number]: ListItemModel } = {};
  return (dispatch: any) => {
    dispatch(setIsPending(true));
    dispatch(setError());
    dispatch(setSuccess());
    ListsService.getList(id)
      .then((response: any) => {
        dispatch(setIsPending(false));
        dispatch(clearCurrentList());
        dispatch(setCurrentListNumber(response.listNumber));
        dispatch(setCurrentListFavorite(response.favorite));
        dispatch(setCurrentListName(response.name));
        for (const key in response.listItems) {
          if (response.listItems.hasOwnProperty(key)) {
            items = {
              ...items,
              [response.listItems[key].listItemNumber]: {
                ...INITIAL_ITEM,
                ...response.listItems[key],
                quantity:
                  response.listItems[key].quantity !== null ||
                  response.listItems[key].quantity > 0
                    ? response.listItems[key].quantity
                    : 1
              }
            };
          }
        }
        dispatch(setItems(items));
      })
      .catch((error: any) => {
        dispatch(setIsPending(false));
        dispatch(
          setError({
            hasFailed: true,
            message: 'Error getting list info'
          })
        );
        console.error('ERROR', error);
      });
  };
};

const getLists = () => {
  return (dispatch: any, getState: any) => {
    const state = getState();
    const alreadyLoading = listsIsPendingListsSel(state);
    // Avoid repeatedly calling the same service
    if (alreadyLoading) {
      return;
    }

    dispatch(setIsPending(true));
    dispatch(setIsPendingLists(true));
    dispatch(setError());
    dispatch(setSuccess());
    ListsService.getLists()
      .then((response: any) => {
        dispatch(setIsPending(false));
        dispatch(setIsPendingLists(false));
        dispatch(setLists(response.data));
      })
      .catch((error: any) => {
        dispatch(setIsPending(false));
        dispatch(setIsPendingLists(false));
        dispatch(
          setError({
            hasFailed: true,
            message: 'Error getting lists info'
          })
        );
        console.error('ERROR', error);
      });
  };
};
const ClearLists = (itemId: number) => {
  return (dispatch: any) => {
    // Call to API to add the item

    dispatch(setError());
    dispatch(
      setSuccess({
        hasRemovedSucced:false,
        hasSucced: false,
        message: ''
      })
    );
    ListsService.clearListService(itemId)
      .then((response: any) => {
        dispatch(getList(itemId));
      })
      .catch((error: any) => {
        dispatch(
          setError({
            hasFailed: true,
            message: 'Error deleting item'
          })
        );
        console.error('ERROR', error);
      });
  };
};

const createList = (
  name: string,
  callback?: (err?: any, newList?: ListModel) => void
) => {
  return (dispatch: any) => {
    dispatch(setIsPending(true));
    dispatch(setError());
    dispatch(setSuccess());
    ListsService.createList(name)
      .then((response: any) => {
        dispatch(setIsPending(false));
        dispatch(clearCurrentList());
        dispatch(getLists());
        dispatch(setCurrentListNumber(response.data.listNumber));
        dispatch(setCurrentListFavorite(response.data.favorite));
        dispatch(setCurrentListName(response.data.name));
        if (callback) {
          callback(null, response.data);
        }
      })
      .catch((error: any) => {
        dispatch(setIsPending(false));
        dispatch(
          setError({
            hasFailed: true,
            message:
              'Error creating list. ' +
              (error.response ? error.response.data.message : error)
          })
        );
        console.error('ERROR', error);
        if (callback) {
          callback(error);
        }
      });
  };
};

const updateList = (id: number, name: string) => {
  return (dispatch: any) => {
    dispatch(setIsUpdating(true));
    dispatch(setError());
    dispatch(setSuccess());
    ListsService.updateList(id, name)
      .then((response: any) => {
        dispatch(setIsUpdating(false));
        dispatch(getLists());
        dispatch(setCurrentListNumber(response.data.listNumber));
        dispatch(setCurrentListFavorite(response.data.favorite));
        dispatch(setCurrentListName(response.data.name));
      })
      .catch((error: any) => {
        dispatch(setIsUpdating(false));
        dispatch(
          setError({
            hasFailed: true,
            message:
              'Error renaming list. ' +
              (error.response ? error.response.data.message : error)
          })
        );
        console.error('ERROR', error);
      });
  };
};

const deleteList = (id: number) => {
  return (dispatch: any) => {
    dispatch(setIsPending(true));
    dispatch(setError());
    dispatch(setSuccess());
    ListsService.deleteList(id)
      .then((response: any) => {
        dispatch(setIsPending(false));
        dispatch(getLists());
        dispatch(clearCurrentList());
      })
      .catch((error: any) => {
        dispatch(setIsPending(false));
        dispatch(
          setError({
            hasFailed: true,
            message:
              'Error deleting list. ' +
              (error.response ? error.response.data.message : error)
          })
        );
        console.error('ERROR', error);
      });
  };
};

const createItem = (
  listId: number,
  itemName: string,
  manufacturerCode: string,
  quantity?: number,
  shipmentMarking?: string,
  warrantyProvider?: string,
  dispatchStr?: string,
  callback?: (err?: any) => void
) => {
  return (dispatch: any) => {
    dispatch(setIsUpdating(true));
    dispatch(setError());
    dispatch(setSuccess());
    ListsService.addItem(
      listId,
      itemName,
      manufacturerCode,
      quantity,
      shipmentMarking,
      warrantyProvider,
      dispatchStr
    )
      .then(() => {
        dispatch(setIsUpdating(false));
        if (callback) {
          callback();
        }
      })
      .catch((error: any) => {
        dispatch(setIsUpdating(false));
        dispatch(
          setError({
            hasFailed: true,
            message: error.response ? error.response.data.message : error
          })
        );
        if (callback) {
          callback(error);
        }
      });
  };
};

const updateItem = (
  itemId: number,
  name: string,
  mfc: string,
  quantity: number,
  shipmentMarking: string,
  warrantyProvider: string,
  claims: string
) => {
  return (dispatch: any) => {
    dispatch(setIsUpdating(true));
    dispatch(setError());
    dispatch(setSuccess());
    ListsService.updateItem(
      itemId,
      name,
      mfc,
      quantity,
      shipmentMarking,
      warrantyProvider,
      claims
    )
      .then((response: any) => {
        dispatch(setIsUpdating(false));
        const storeData: any = store.getState();
        const newItem: ListItemModel = {
          ...storeData.lists.currentList.listItems[itemId]
        };
        newItem.total =
          newItem.price !== null ? newItem.price * quantity : null;
        newItem.dispatch = response.dispatch;
        newItem.shipmentMarking = response.shipmentMarking;
        newItem.quantity = response.quantity;
        newItem.warrantyProvider = response.warrantyProvider;
        dispatch(changeItem(newItem));
      })
      .catch((error: any) => {
        dispatch(setIsUpdating(false));
        dispatch(
          setError({
            hasFailed: true,
            message:
              'Error updating item. ' +
              (error.response ? error.response.data.message : error)
          })
        );
      });
  };
};

const deleteItem = (itemId: number) => {
  return (dispatch: any) => {
    dispatch(setIsUpdating(true));
    dispatch(setError());
    dispatch(setSuccess());
    ListsService.deleteItem(itemId)
      .then((response: any) => {
        dispatch(setIsUpdating(false));
        dispatch(removeItem(itemId));
        dispatch(
          setSuccess({
            hasRemovedSucced:true,
            hasSucced: false,
            message: 'Item has been removed.'
          })
        );
      })
      .catch((error: any) => {
        dispatch(setIsUpdating(false));
        dispatch(
          setError({
            hasFailed: true,
            message:
              'Error deleting item. ' +
              (error.response ? error.response.data.message : error)
          })
        );
        console.error('ERROR', error);
      });
  };
};

const updateItemsDetails = (itemsIds: number[]) => {
  return (dispatch: any) => {
    const storeData: any = store.getState();
    const itemsArray: Array<{
      productNumber: string;
      manufacturerCode: string;
    }> = [];
    const newItems: any[] = storeData.lists.currentList.listItems;
    itemsIds.forEach(item => {
      const newItem: ListItemModel =
        storeData.lists.currentList.listItems[item];
      itemsArray.push({
        productNumber: newItem.name,
        manufacturerCode: newItem.manufacturerCode
      });
    });
    if (itemsArray.length > 0) {
      ProductService.getProductsSearch(itemsArray).then(response => {
        response.forEach((item, i) => {
          newItems[itemsIds[i]].itemDetailsState = 'loaded';
          newItems[itemsIds[i]].inStock = item.inStock;
          newItems[itemsIds[i]].total =
            item.partnerPrice *
            (newItems[itemsIds[i]].quantity > 0
              ? newItems[itemsIds[i]].quantity
              : 1);
          newItems[itemsIds[i]].price = item.partnerPrice;
          newItems[itemsIds[i]].retailPrice = item.retailPrice;
          newItems[itemsIds[i]].image = item.imageUrls && item.imageUrls[0];
          newItems[itemsIds[i]].description = item.description;
          newItems[itemsIds[i]].replacementMfc = item.replacementMfc;
          newItems[itemsIds[i]].replacementPart = item.replacementPart;
          newItems[itemsIds[i]].alternateProductManufacturer =
            item.alternateProductManufacturer;
          newItems[itemsIds[i]].alternateProductNumber =
            item.alternateProductNumber;
          newItems[itemsIds[i]].oemProductNumber = item.oemProductNumber;
          newItems[itemsIds[i]].oemProductManufacturer =
            item.oemProductManufacturer;
          newItems[itemsIds[i]].warehouseDefault =
            item.warehouse;
        });
        dispatch(setItems(newItems));
      });
    }
  };
};

const updateItemDetails = (itemId: number) => {
  return (dispatch: any) => {
    const storeData: any = store.getState();
    const newItem: ListItemModel =
      storeData.lists.currentList.listItems[itemId];
    if (newItem.itemDetailsState === 'pending') {
      ProductService.getProductsSearch([
        {
          productNumber: newItem.name,
          manufacturerCode: newItem.manufacturerCode
        }
      ])
        .then(response => {
          newItem.itemDetailsState = 'loaded';
          newItem.inStock = response[0].inStock;
          newItem.total =
            response[0].partnerPrice *
            (newItem.quantity > 0 ? newItem.quantity : 1);
          newItem.price = response[0].partnerPrice;
          newItem.retailPrice = response[0].retailPrice;
          newItem.image = response[0].imageUrls[0];
          newItem.description = response[0].description;
          newItem.replacementMfc = response[0].replacementMfc;
          newItem.replacementPart = response[0].replacementPart;
          newItem.alternateProductManufacturer =
            response[0].alternateProductManufacturer;
          newItem.alternateProductNumber = response[0].alternateProductNumber;
          newItem.oemProductNumber = response[0].oemProductNumber;
          newItem.oemProductManufacturer = response[0].oemProductManufacturer;
          dispatch(changeItem(newItem));
        })
        .catch((error: any) => {
          newItem.itemDetailsState = 'none';
          dispatch(changeItem(newItem));
        });
    }
  };
};

const getTopProductsList = () => {
  let items: { [key: number]: ListItemModel } = {};
  return (dispatch: any) => {
    dispatch(setIsPending(true));
    dispatch(setError());
    dispatch(setSuccess());
    TopProductsService.getTopProducts(50)
      .then((response: any) => {
        dispatch(setIsPending(false));
        dispatch(clearCurrentList());
        dispatch(setCurrentListNumber(0));
        dispatch(setCurrentListFavorite(false));
        dispatch(setCurrentListName(`${i18n.t('common.productsOnSale')}`));
        response.forEach((part: any) => {
          const newItem: ListItemModel = { ...INITIAL_ITEM };
          newItem.itemDetailsState = 'loaded';
          newItem.listItemNumber = part.productNumber;
          newItem.name = part.productNumber;
          newItem.manufacturerCode = part.manufacturerCode;
          newItem.inStock = part.inStock;
          newItem.total = part.partnerPrice;
          newItem.price = part.partnerPrice;
          newItem.retailPrice = part.retailPrice;
          newItem.image = part.imageUrls ? part.imageUrls[0] : '';
          newItem.description = part.description;
          newItem.replacementMfc = part.replacementMfc;
          newItem.replacementPart = part.replacementPart;
          newItem.alternateProductNumber = part.alternateProductNumber;
          newItem.alternateProductManufacturer =
            part.alternateProductManufacturer;
          newItem.oemProductNumber = part.oemProductNumber;
          newItem.oemProductManufacturer = part.oemProductManufacturer;
          items = { ...items, [newItem.listItemNumber]: newItem };
        });
        dispatch(setItems(items));
      })
      .catch((error: any) => {
        dispatch(setIsPending(false));
        dispatch(
          setError({
            hasFailed: true,
            message:
              'Error getting top parts list. ' +
              (error.response ? error.response.data.message : error)
          })
        );
        console.error('ERROR', error);
      });
  };
};

const getCommonPurchasesList = () => {
  let items: { [key: number]: ListItemModel } = {};
  return (dispatch: any) => {
    dispatch(setIsPending(true));
    dispatch(setError());
    dispatch(setSuccess());
    FrequentlyPurchasedService.getProducts(50)
      .then((response: any) => {
        dispatch(setIsPending(false));
        dispatch(clearCurrentList());
        dispatch(setCurrentListNumber(0));
        dispatch(setCurrentListFavorite(false));
        dispatch(setCurrentListName(`${i18n.t('myFavourites.myFrequentPurchases')}`));
        response.forEach((part: any) => {
          const newItem: ListItemModel = { ...INITIAL_ITEM };
          newItem.itemDetailsState = 'loaded';
          newItem.listItemNumber = part.productNumber;
          newItem.name = part.productNumber;
          newItem.manufacturerCode = part.manufacturerCode;
          newItem.inStock = part.inStock;
          newItem.total = part.partnerPrice;
          newItem.price = part.partnerPrice;
          newItem.retailPrice = part.retailPrice;
          newItem.image = part.imageUrls ? part.imageUrls[0] : '';
          newItem.description = part.description;
          newItem.replacementMfc = part.replacementMfc;
          newItem.replacementPart = part.replacementPart;
          newItem.alternateProductManufacturer =
            part.alternateProductManufacturer;
          newItem.alternateProductNumber = part.alternateProductNumber;
          newItem.oemProductNumber = part.oemProductNumber;
          newItem.oemProductManufacturer = part.oemProductManufacturer;
          newItem.warehouseDefault = part.warehouse;
          items = { ...items, [newItem.listItemNumber]: newItem };
        });
        dispatch(setItems(items));
      })
      .catch((error: any) => {
        dispatch(setIsPending(false));
        dispatch(
          setError({
            hasFailed: true,
            message:
              'Error getting top parts list. ' +
              (error.response ? error.response.data.message : error)
          })
        );
        console.error('ERROR', error);
      });
  };
};
const clearRemoveAlert = () => {
  return (dispatch: any, getState: any) => {
    dispatch(
      setSuccess({
        hasRemovedSucced:false,
        hasSucced: false,
        message: ''
      })
    );
  };
};

export default {
  getLists,
  getList,
  clearCurrentList,
  createList,
  ClearLists,
  deleteList,
  updateList,
  createItem,
  updateItem,
  deleteItem,
  updateItemDetails,
  updateItemsDetails,
  getTopProductsList,
  getCommonPurchasesList,
  setError,
  clearRemoveAlert
};
